import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { EmptyTableMessage } from "@/core/components/empty-table-message";
import { SimpleTable } from "@/core/components/simple-table";
import { Stack } from "@/core/components/ui/stack";
import { CUSTOMER_TABS_LABELS } from "@/business-settings/customers/constants";
import { dayjs } from "@/core/utils/date";
import { cn } from "@/core/utils/utils";
import { CUSTOMER_STATUS } from "@/customer/constants";
import {
  ICustomer,
  CustomerWithReferralsAndPayouts,
} from "@/customer/customer";
// import { ActionsCol } from "./cols/actions-col";
import { AttributedToCol } from "./cols/attributed-to-col";
import { CustomerNameCol } from "./cols/customer-name-col";
import { JoinedCol } from "./cols/joined-col";
import { PayoutsCol } from "./cols/payouts-col";
import { PhoneCol } from "./cols/phone-col";
import { ReferralsCol } from "./cols/referrals-col";
import { ReferredCol } from "./cols/referred-col";

export type CustomerType = "leads" | "advocates";

interface CustomersTableProps {
  customers: CustomerWithReferralsAndPayouts[];
  bottomContent?: React.ReactNode;
  isLoading?: boolean;
  manualSorting?: boolean;
  onRowClick?: (customer: ICustomer) => void;
  selectedCustomerId?: string;
  scrollResetKey?: string;
  customerType?: CustomerType;
  className?: string;
}

export const CustomersTable = ({
  customers,
  bottomContent,
  isLoading,
  manualSorting,
  onRowClick,
  selectedCustomerId,
  scrollResetKey,
  customerType,
  className,
}: CustomersTableProps) => {
  const filteredSortedCustomers = useMemo(() => {
    if (!customerType) return customers;

    return customers
      .filter((customer) => {
        if (customerType === "leads") {
          return customer.status === CUSTOMER_STATUS.POTENTIAL;
        } else if (customerType === "advocates") {
          return customer.status === CUSTOMER_STATUS.ACTIVE;
        }
        return true;
      })
      .sort(
        (a, b) => dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf(),
      );
  }, [customers, customerType]);

  const isAdvocate = customerType === "advocates";

  const columns = useMemo(
    () =>
      [
        CustomerNameCol(),
        PhoneCol(),
        isAdvocate && AttributedToCol(),
        isAdvocate && ReferralsCol(),
        isAdvocate && PayoutsCol(),
        isAdvocate ? JoinedCol() : ReferredCol(),
        // ActionsCol(), Add back when there are additional actions to take on a customer
      ].filter(Boolean) as ColumnDef<CustomerWithReferralsAndPayouts>[],
    [isAdvocate],
  );

  if (filteredSortedCustomers.length === 0 && !isLoading) {
    return (
      <Stack gap="sm" className="w-full pr-5" fullWidth>
        <EmptyTableMessage className="w-full">
          No{" "}
          {customerType
            ? CUSTOMER_TABS_LABELS[customerType].toLowerCase()
            : "customers"}{" "}
          found.
        </EmptyTableMessage>
        {bottomContent && (
          <Stack
            direction="row"
            justify="center"
            align="center"
            className="w-full flex-wrap pb-6"
          >
            {bottomContent}
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <SimpleTable
      data={filteredSortedCustomers}
      columns={columns}
      isLoading={isLoading}
      manualSorting={manualSorting}
      onRowClick={onRowClick}
      selectedRowId={selectedCustomerId}
      scrollResetKey={scrollResetKey}
      bottomContent={bottomContent}
      className={cn("min-h-0 w-full flex-1 overflow-hidden", className)}
    />
  );
};
